import Analytics from '@/utils/analytics';
import EditIcon from '@assets/icons/edit.svg';
import i18n from '@/utils/i18n';
import moment from 'moment';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ScanIcon from '@assets/icons/scan.svg';
import {
  Action,
  CONTENT_HEIGHT,
  CONTENT_PADDING_TOP,
  ICON_BUTTON_HIT_SLOP,
} from '@/components/navigation-header-content';
import { ActivityIndicator } from '@/components/activity-indicator';
import { AddNoteRightAction } from '@/components/order-guide/add-note-right-action';
import {
  AllowedShortcutNumbers,
  AllowedShortcuts,
  numberKeyMap,
} from '@/platform/shortcuts/operator-shortcuts';
import { APP_ROUTES, ORDER_GUIDE_ROUTES } from '@/navigators/routes';
import {
  ApplicationType,
  OrderGuideCarouselConfigType,
  OrderGuideCarouselSettings,
  OrderGuideCarouselType,
} from '@/contexts/dynamic-config';
import { AppStackParams, OrderGuideStackParams } from '@/navigators/types';
import {
  BORDER_RADIUS_SMALL,
  FONTS,
  LARGE_LAYOUT_CONTENT_MAX_WIDTH,
  SCREEN_PADDING,
  SCREEN_PADDING_LARGE,
  SCREEN_PADDING_SMALL,
  TYPOGRAPHY,
} from '@/styles';
import {
  BrandPageCarouselFieldsFragment,
  ItemsContext_VariantsPackFieldsFragment,
  OrderGuideSortOrder,
} from '@/types';
import { BrandsCarousel } from '@/components/brands-carousel';
import { Button } from '@/components/button';
import {
  cancelSpan,
  endSpan,
  startSpan,
} from '@/analytics/fennel/performance/default-tracker';
import { CatalogItemCarousel } from '@/components/catalog-item-carousel';
import { CategorySelector } from '@/components/partners/category-selector';
import {
  ChatStatus,
  OrderGuideMergeStrategy,
  SampleRequestStatus,
} from '@/enums';
import { CompositeNavigationProp, RouteProp } from '@react-navigation/native';
import { createStyles } from '@/contexts/theme-context';
import { DisplayAd } from '@/components/ads/display-ad';
import { DSRToolingActionSheet } from '@/components/orders/dsr-tooling-action-sheet';
import { EmptyOrderGuideContent } from '@/components/order-guide/empty-order-guide-content';
import { EssentialItemRightAction } from '@/components/order-guide/essential-item-right-action';
import {
  Feature,
  setFeatureSpanAttributes,
  SpanAttribute,
} from '@/utils/sentry/attributes';
import {
  findFirstPaidSearchSku,
  isSampleRequestLimitReached,
} from '@/utils/ads';
import { FulfillmentAvailability } from '@/components/partners/fulfillment-availability';
import { FulfillmentPill } from '@/components/order-guide/fulfillment-pill';
import { getSearchFieldsFromMetadata } from '@/contexts/item-context-utils';
import { LIST_HEIGHT as HORIZONTAL_SELECTOR_HEIGHT } from '@/components/horizontal-selector';
import {
  INNER_HORIZONTAL_PADDING,
  OrderableItemListItemWithRowQuantity,
} from '@/components/orders/orderable-item-list-item';
import { ItemGroupSelector } from '@/components/item-group-selector';
import {
  ItemListOption,
  ItemListOptionMetadata,
} from '@/contexts/item-context-types';
import { ItemSearchHeaderAction } from '@/components/item-search-header-action';
import { Key } from '@platform/shortcuts/utils/keyboard-platform';
import {
  Props as ListItemProps,
  SwipeableWithInstantClose,
} from '@/components/list-item';
import { ListItemType, RecyclerListRef } from '@/components/recycler-list';
import { LoadingText } from '@/components/loading-text';
import { MultiOrderGuideButton } from '@/components/orders/multi-order-guide-button';
import { MultiOrderGuideTitle } from '@/components/order-guide/multi-order-guide-title';
import { OrderableItemScrollbarContainer } from '@/components/orderable-item-scrollbar-container';
import {
  OrderGuideCarouselOption,
  OrderGuideItemListOption,
  OrderGuideItemSection,
  OrderGuideList,
} from '@/components/orders/order-guide-list';
import { OrderGuideListItem } from '@/components/orders/order-guide-list-item';
import { OrderGuidePartitionSectionActionSheet } from '@/components/order-guide/order-guide-partition-section-sheet';
import { OrderGuideSettingsActionSheet } from '@/components/order-guide/order-guide-settings-action-sheet';
import {
  PageContainer,
  Props as PageContainerProps,
  PageContainerScrollRef,
} from '@/components/page-container';
import { PaidSearchItemHeader } from '@/components/ads/paid-search-item-header';
import { PartitionSelector } from '@/components/order-guide/partition-selector';
import { PerfTiming, PerfTimingEvent } from '@/utils/perf-timing';
import {
  PixelRatio,
  Platform,
  ScrollView,
  Text,
  TextStyle,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import {
  PromotionListOption,
  usePromotionListOptions,
} from '@/hooks/use-promotion-list-options';
import { SampleRequestFollowUp } from '@/components/ads/sample-request-follow-up';
import { ScannerOperationsActionSheet } from '@/components/scanner-operations-action-sheet';
import {
  SEARCH_FILTER_DEBOUNCE_DELAY_MS,
  useMultiSearchFilters,
} from '@/hooks/use-multi-search-filters';
import { SearchOrderGuideInput } from '@/components/order-guide/search-order-guide-input';
import { SharePriceQuoteActionSheet } from '@/components/partners/share-price-quote-action-sheet';
import { SharePriceQuoteButton } from '@/components/partners/share-price-quote-button';
import { SmartOrderActionSheet } from '@/components/orders/smart-order-action-sheet';
import { SmartOrderButton } from '@/components/orders/smart-order-button';
import { StackNavigationProp } from '@react-navigation/stack';
import { TimeToFullDisplay } from '@/utils/sentry/platform';
import { Type } from '@/components/toast';
import { useAdaptAndGroupVariantPacks } from '@/hooks/use-adapt-and-group-item-list-options';
import { useAdsContext } from '@/contexts/ads-context';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useCartRedirect } from '@/hooks/insights-hub/use-cart-redirect';
import { useDebouncedCallback } from 'use-debounce';
import {
  useDynamicConfigValue,
  useSupplierDynamicConfigFlag,
  useSupplierDynamicConfigValue,
} from '@/hooks/use-dynamic-config-value';
import { useGetBrandsCarousel } from '@/hooks/use-get-brands-carousel';
import { useHasAnyItemQuantity } from '@/stores/new-order-quantity-store';
import { useHeaderRestaurantSelectorProps } from '@/components/use-header-restaurant-selector-props';
import { useItemGroupCategoryFilters } from '@/hooks/use-item-group-category-filters';
import { useItemRecommendationsContext } from '@/contexts/item-recommendations-context';
import { useKeyboardShortcuts } from '@platform/shortcuts/hooks/use-keyboard-shortcuts';
import { useLayoutContext } from '@/contexts/layout-context';
import { useNewOrderActionsContext } from '@/contexts/new-order-actions-context';
import { useNewOrderContext } from '@/contexts/new-order-context';
import { useOfflineModeActive } from '@/hooks/use-offline-mode-active';
import { useOrderableItemColumnCount } from '@/components/orders/orderable-item-list';
import { useOrderGuideItemsContext } from '@/contexts/order-guide-items-context';
import { useOrderGuideSettingsContext } from '@/contexts/order-guide-settings-context';
import { useOrderGuideStore } from '@/stores/order-guide-store';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useScannerOperations } from '@/hooks/use-scanner-operations';
import { useSearchCatalog } from '@/hooks/use-search-catalog';
import { useShowListView } from '@/hooks/use-show-list-view';
import { useTabBlurListener } from '@/hooks/use-tab-blur-listener';
import { useToasts } from '@/contexts/toast-context';
import { useUpdateOrderGuideItemContext } from '@/contexts/update-order-guide-item-context';
import { useVariantPackInfoContext } from '@/contexts/variant-pack-info-context';
import { ViewOrderButton } from '@/components/partners/view-order-button';
import { VolumePromotionsCarousel } from '@/components/orders/volume-promotions-carousel';

const CATALOG_SEARCH_RESULT_KEY = 'CATALOG_SEARCH_RESULTS';
const EMPTY_ORDER_GUIDE_KEY = 'EMPTY_ORDER_GUIDE';
const PAID_SEARCH_RESULTS_KEY = 'PAID_SEARCH_RESULTS_KEY';

interface Props {
  navigation: CompositeNavigationProp<
    CompositeNavigationProp<
      StackNavigationProp<OrderGuideStackParams, ORDER_GUIDE_ROUTES.Base>,
      StackNavigationProp<AppStackParams, APP_ROUTES.OrderGuide>
    >,
    StackNavigationProp<OrderGuideStackParams, ORDER_GUIDE_ROUTES.Scan> &
      StackNavigationProp<AppStackParams, APP_ROUTES.Catalog>
  >;
  route: RouteProp<OrderGuideStackParams, ORDER_GUIDE_ROUTES.Base>;
}

const BARCODE_SCANNER_HIT_SLOP = {
  bottom: 8,
  left: 16,
  right: 16,
  top: 8,
};

const SEARCH_BAR_VERTICAL_MARGIN = 8;
const TEXT_INPUT_HEIGHT = 42 * PixelRatio.getFontScale();
const SEARCH_BAR_HEIGHT = TEXT_INPUT_HEIGHT + 2 * SEARCH_BAR_VERTICAL_MARGIN;
const LARGE_LAYOUT_NAV_PADDING_BOTTOM = 16;
const CATALOG_LIST_VIEW_MARGIN =
  SCREEN_PADDING_LARGE - INNER_HORIZONTAL_PADDING;
const LATEST_SAMPLE_FOLLOW_UP_DAYS = 30;

const getSearchableFields = (item: ItemListOption) => {
  return getSearchFieldsFromMetadata(item);
};

const getPromoSearchableFields = (item: PromotionListOption) => {
  return item.metadata?.item
    ? getSearchFieldsFromMetadata(item.metadata.item)
    : [];
};

const getDisplayAdSearchableFields = (
  variantPack: ItemsContext_VariantsPackFieldsFragment,
) => {
  return [
    variantPack.item.display_name,
    variantPack.external_item_id || '',
    variantPack.item.description || '',
    variantPack.item.category || '',
    variantPack.pack.unit,
    variantPack.item.extra_keywords || '',
  ];
};

export const MultiOrderGuideScreen: React.FC<Props> = ({
  navigation,
  route,
}) => {
  const loadSpanRef =
    useRef<ReturnType<typeof startSpan<'Span::order_guide::load'>>>(undefined);

  useEffect(() => {
    loadSpanRef.current = startSpan('Span::order_guide::load');
    return () => {
      cancelSpan(loadSpanRef.current);
    };
  }, []);

  const orderGuideCollectionUUID = route.params?.orderGuideCollectionUUID;
  const orderGuideCarouselConfig = useDynamicConfigValue(
    'order_guide_carousel_config',
  );
  const orderGuideCatalogSearchEnabled = useSupplierDynamicConfigValue(
    'order_guide_catalog_search_enabled',
  );
  const isOfflineModeActive = useOfflineModeActive();
  const hasAnyItemQuantity = useHasAnyItemQuantity();
  const { isSmallLayout, isSmallTabletLayout } = useLayoutContext();
  const { top } = useSafeAreaInsets();
  const {
    customOrderGuideEditingEnabled,
    orderGuideMergeStrategy,
    quantitiesHydrated,
  } = useNewOrderContext();
  const { selectedRestaurantRelationship, supplierEmployeeRole, user } =
    useAuthenticatedUserContext();
  const { debouncedUpdateQuantity } = useNewOrderActionsContext();
  const {
    categories,
    categoryFilter,
    categoryItemGroups,
    categoryItemGroupFilter,
    collections,
    hasResponse,
    loading: orderGuideItemsLoading,
    partitionFilter,
    partitionFilters,
    sections,
    selectedCollection,
    setCategoryFilter,
    setCategoryItemGroupFilter,
    setPartitionFilter,
    variantPackPartitionMap,
  } = useOrderGuideItemsContext();

  const canSwitchCollections =
    customOrderGuideEditingEnabled ||
    (orderGuideMergeStrategy === OrderGuideMergeStrategy.DISTRIBUTOR_ONLY &&
      collections.length > 1);
  const orderGuideItemCount = useOrderGuideStore(
    (state) => state.orderGuideItems.length,
  );

  const orderGuideUnifiedSearchEnabled = useSupplierDynamicConfigValue(
    'order_guide_unified_search_enabled',
  );
  const prominentScanningEntryEnabled = useSupplierDynamicConfigFlag(
    'on_device_scanning_config',
    'prominent_scanning_entry_enabled',
  );

  const {
    setSelectedCollectionUUID,
    orderGuideSortOrder,
    showCategoryFilters,
  } = useOrderGuideSettingsContext();

  const { editEssentialItem, editItemNotes, isRowEssential } =
    useUpdateOrderGuideItemContext();

  useEffect(() => {
    if (
      orderGuideCollectionUUID &&
      orderGuideCollectionUUID !== selectedCollection?.uuid
    ) {
      setSelectedCollectionUUID(orderGuideCollectionUUID);
      navigation.setParams({ orderGuideCollectionUUID: undefined });
    }
  }, [
    navigation,
    orderGuideCollectionUUID,
    selectedCollection?.uuid,
    setSelectedCollectionUUID,
  ]);

  const { displayAd, displayAdLoading, sampleRequestMap } = useAdsContext();
  const adaptAndGroupVariantPacks = useAdaptAndGroupVariantPacks();
  const { showErrorToast, showToast } = useToasts();
  const brandCarouselItems = useGetBrandsCarousel(
    !Boolean(orderGuideCarouselConfig?.BRAND),
  );
  const [sampleRequestToFollowUp, setSampleRequestToFollowUp] = useState<
    ItemListOption | undefined
  >();
  const [oneFollowUpSubmitted, setOneFollowUpSubmitted] = useState(false);
  const [showScanSelectionActionSheet, setShowScanSelectionActionSheet] =
    useState(false);

  const scannerOperations = useScannerOperations(ORDER_GUIDE_ROUTES);
  const { itemRecommendations } = useItemRecommendationsContext();
  const { height } = useWindowDimensions();

  const { hasLastPurchaseInfoResponse } = useVariantPackInfoContext();
  const [styles, theme] = useStyles();
  const { showListView } = useShowListView();

  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [showSmartOrderSheet, setShowSmartOrderSheet] = useState(false);
  const [orderGuideVisible, setOrderGuideVisible] = useState(false);
  const [
    partitionSelectorVariantPackUUID,
    setPartitionSelectorVariantPackUUID,
  ] = useState<string | null>(null);
  const [debouncedSearchCatalogLoading, setDebouncedSearchCatalogLoading] =
    useState(false);
  const swipeableItemRef = useRef<SwipeableWithInstantClose>(null);
  const pageContainerRef = useRef<PageContainerScrollRef>(null);

  const [
    dsrToolingActionSheetItemMetadata,
    setDSRToolingActionSheetItemMetadata,
  ] = useState<ItemListOptionMetadata | null>(null);
  const [isSharePriceQuoteOpen, setIsSharePriceQuoteOpen] = useState(false);

  useCartRedirect();

  const loading =
    orderGuideItemsLoading ||
    displayAdLoading ||
    // Wait until we know what quantities to render for the list items
    !quantitiesHydrated ||
    !hasResponse ||
    (orderGuideSortOrder === OrderGuideSortOrder.PurchasedDate &&
      !hasLastPurchaseInfoResponse);

  const closeSwipeable = useCallback(() => {
    swipeableItemRef.current?.close();
    swipeableItemRef.current = null;
  }, []);

  const { categoryItemGroupsInSelector } = useItemGroupCategoryFilters({
    categoryUUIDsToFilterFrom: categoryItemGroups.map(
      (itemGroup) => itemGroup.uuid!,
    ),
  });

  const itemGroupsEnabled = useDynamicConfigValue('item_groups_enabled');
  const {
    clearSearch: clearOrderGuideSearch,
    onDisplayTextChange,
    searching: searchingOrderGuide,
    searchText,
    sectionData,
  } = useMultiSearchFilters<ItemListOption>({
    data: sections,
    getSearchableFields,
    sectionList: true,
  });

  const onlyHasDefaultPartition = useMemo(() => {
    return partitionFilters.every((p) => p.metadata?.isDefault);
  }, [partitionFilters]);

  const { searchItems: searchCatalog, items: catalogSearchResults } =
    useSearchCatalog({
      searchSource: 'multi_order_guide',
    });

  const variantPacksInSelectedCollection = useMemo<Set<string>>(() => {
    const selectedCollectionPartitionUUIDs = new Set(
      (selectedCollection?.partitions || []).map((p) => p.uuid),
    );

    return Object.keys(variantPackPartitionMap).reduce((acc, vpUUID) => {
      const partitionToOGMap = variantPackPartitionMap[vpUUID] || {};
      const partitionUUIDs = Object.keys(partitionToOGMap);
      if (partitionUUIDs.some((p) => selectedCollectionPartitionUUIDs.has(p))) {
        acc.add(String(vpUUID));
      }
      return acc;
    }, new Set<string>());
  }, [selectedCollection, variantPackPartitionMap]);

  const [catalogSearchItems, paidSearchItems] = useMemo<
    [ItemListOption[] | null, ItemListOption[] | null]
  >(() => {
    if (!Array.isArray(catalogSearchResults)) {
      return [null, null];
    }
    const catalogResults = [];
    const paidSearchResults = [];

    for (let searchResult of catalogSearchResults) {
      if (searchResult.metadata?.paidSearch) {
        paidSearchResults.push(searchResult);
        continue;
      }

      if (
        searchResult.metadata?.skus.length &&
        searchResult.metadata?.skus.length > 1
      ) {
        const filteredSkus = searchResult.metadata.skus.filter(
          (sku) => !variantPacksInSelectedCollection.has(sku.variantPackUUID),
        );

        if (filteredSkus.length) {
          catalogResults.push({
            ...searchResult,
            metadata: {
              ...searchResult.metadata,
              skus: filteredSkus,
            },
          });
        }
      } else if (
        !variantPacksInSelectedCollection.has(String(searchResult.value))
      ) {
        catalogResults.push(searchResult);
      }
    }

    return [catalogResults, paidSearchResults];
  }, [catalogSearchResults, variantPacksInSelectedCollection]);

  const debouncedSearchCatalog = useDebouncedCallback(async (input: string) => {
    try {
      await searchCatalog(input);
    } catch (e) {
      showErrorToast();
    } finally {
      setDebouncedSearchCatalogLoading(false);
    }
  }, SEARCH_FILTER_DEBOUNCE_DELAY_MS);

  const promotions = usePromotionListOptions();
  const {
    clearSearch: clearPromotionSearch,
    filteredData: filteredPromotions,
    onDisplayTextChange: setPromotionFilterText,
    searching: searchingPromotions,
  } = useMultiSearchFilters<PromotionListOption>({
    data: promotions,
    getSearchableFields: getPromoSearchableFields,
  });

  const visiblePromotionUUIDs = useMemo(() => {
    return new Set(filteredPromotions.map((p) => String(p.value)));
  }, [filteredPromotions]);

  const {
    clearSearch: clearDisplayAdSearch,
    filteredData: filteredDisplayAdVPs,
    onDisplayTextChange: setAdFilterText,
    searching: searchingDisplayAd,
  } = useMultiSearchFilters({
    data: displayAd?.variantPacks || [],
    getSearchableFields: getDisplayAdSearchableFields,
  });

  const samples = useMemo(
    () =>
      Object.values(sampleRequestMap).reduce<{
        notRequested: ItemListOption[];
        requested: ItemListOption[];
      }>(
        (acc, item, currentIndex, array) => {
          if (item && item.variant_pack) {
            const adaptedVariantPack = adaptAndGroupVariantPacks([
              item.variant_pack,
            ])[0];
            if (!item.sample_request) {
              if (!isSampleRequestLimitReached(item)) {
                acc.notRequested.push(adaptedVariantPack);
              }
            } else {
              acc.requested.push(adaptedVariantPack);
            }
          }
          if (currentIndex === array.length - 1) {
            return {
              notRequested: acc.notRequested,
              requested: acc.requested.sort((a, b) => {
                const aRequestedAt = moment(
                  a.metadata?.sampleRequest?.sample_request?.requested_at,
                );
                const bRequestedAt = moment(
                  b.metadata?.sampleRequest?.sample_request?.requested_at,
                );
                if (aRequestedAt.isBefore(bRequestedAt)) {
                  return -1;
                } else if (aRequestedAt.isAfter(bRequestedAt)) {
                  return 1;
                }
                return 0;
              }),
            };
          }
          return acc;
        },
        {
          notRequested: [],
          requested: [],
        },
      ),
    [adaptAndGroupVariantPacks, sampleRequestMap],
  );

  useEffect(() => {
    const sampleRequestQualified = samples.requested.find((request) => {
      const { metadata } = request;
      const followUpPeriodDays = metadata?.sampleRequest?.follow_up_period_days;
      const pastSampleRequest = metadata?.sampleRequest?.sample_request!;
      const isApproved =
        pastSampleRequest.status === SampleRequestStatus.APPROVED;
      if (
        followUpPeriodDays &&
        !pastSampleRequest.follow_up_submitted_at &&
        !oneFollowUpSubmitted &&
        isApproved &&
        pastSampleRequest.requested_by?.uuid === user.uuid
      ) {
        const followUpDayEarliest = moment(pastSampleRequest.qualified_at).add(
          followUpPeriodDays,
          'days',
        );
        const followUpDayLatest = moment(pastSampleRequest.qualified_at).add(
          LATEST_SAMPLE_FOLLOW_UP_DAYS,
          'days',
        );
        if (moment().isBetween(followUpDayEarliest, followUpDayLatest)) {
          return request;
        }
      }
      return;
    });
    if (
      sampleRequestQualified &&
      !sampleRequestToFollowUp &&
      !oneFollowUpSubmitted
    ) {
      setSampleRequestToFollowUp(sampleRequestQualified);
    }
  }, [
    oneFollowUpSubmitted,
    samples.requested,
    sampleRequestToFollowUp,
    user.uuid,
  ]);

  const onSampleRequestFollowUpClose = (showSuccessToast = false) => {
    setOneFollowUpSubmitted(true);
    setSampleRequestToFollowUp(undefined);
    if (showSuccessToast) {
      showToast({
        content: i18n.t('toast.follow_up_submitted'),
        type: Type.POSITIVE,
      });
    }
  };

  const visibleDisplayAdUUIDs = useMemo(() => {
    return new Set(filteredDisplayAdVPs.map((p) => String(p.uuid)));
  }, [filteredDisplayAdVPs]);

  const hideDisplayAd =
    !displayAd || !visibleDisplayAdUUIDs.size || partitionFilter;

  const listRef = useRef<RecyclerListRef<ItemListOption> | null>(null);

  const baseNavigationHeaderProps = useHeaderRestaurantSelectorProps();

  const hasNoOrderGuideItems =
    sections.length === 0 ||
    (sections.length === 1 && sections[0].data.length === 0);

  const orderGuideCategoryFilter = itemGroupsEnabled
    ? categoryItemGroupFilter
    : categoryFilter;

  const searchLoading =
    searchingOrderGuide || searchingPromotions || searchingDisplayAd;

  // reset scroll position when finished searching
  useEffect(() => {
    if (!searchLoading) {
      listRef.current?.scrollToTop();
    }
  }, [searchLoading]);

  // rendered when no OG items match search, and user is not opted into catalog search
  // TODO: remove when catalog search is enabled for everyone
  const noOrderGuideSearchResults =
    !searchLoading &&
    sectionData.length === 0 &&
    // promotion list does not render when filtering by partitions
    (visiblePromotionUUIDs.size === 0 || partitionFilter) &&
    searchText;

  const hasNoCatalogItems =
    !debouncedSearchCatalogLoading &&
    Array.isArray(catalogSearchItems) &&
    catalogSearchItems.length === 0;

  const hasCatalogItems =
    !debouncedSearchCatalogLoading &&
    Array.isArray(catalogSearchItems) &&
    catalogSearchItems.length;

  // full empty state only when the order guide is completely empty and there are no search results
  const renderFullEmptyState =
    hasNoOrderGuideItems &&
    !hasCatalogItems &&
    !partitionFilter &&
    !orderGuideCategoryFilter;

  // rendered when the selected partition has no items
  // but do not show if also searching the catalog
  const renderPartitionEmptyState =
    sections.length === 1 &&
    sections[0].data.length === 0 &&
    partitionFilter &&
    !searchText;

  // rendered when user is opted into catalog search, but no catalog items match search
  const renderSearchEmptyStateWithCatalogSearch =
    noOrderGuideSearchResults && hasNoCatalogItems && !paidSearchItems?.length;

  const carouselWrapperStyle = useMemo(() => {
    if (isSmallTabletLayout) {
      return styles.carouselWrapperTablet;
    }
    if (!isSmallLayout) {
      return styles.carouselWrapperLarge;
    }
  }, [isSmallLayout, isSmallTabletLayout, styles]);

  const getCarouselComponentByType = useCallback(
    (
      carouselType: OrderGuideCarouselType,
      titleOverride?: string,
      maxLength?: number,
    ) => {
      switch (carouselType) {
        case OrderGuideCarouselType.BRAND: {
          const carouselItems = brandCarouselItems
            .map((item) => item.brand_page)
            .slice(
              0,
              maxLength ?? brandCarouselItems.length,
            ) as BrandPageCarouselFieldsFragment[];
          return brandCarouselItems.length ? (
            <View
              style={[
                styles.carouselWrapper,
                carouselWrapperStyle,
                styles.brandCarouselStyle,
              ]}
            >
              <BrandsCarousel
                data={carouselItems}
                title={titleOverride ?? i18n.t('check_out_these_brands')}
              />
            </View>
          ) : null;
        }
        case OrderGuideCarouselType.PROMOTION:
          return !hideDisplayAd ? (
            <View style={[styles.carouselWrapper, carouselWrapperStyle]}>
              <VolumePromotionsCarousel
                contentContainerStyle={styles.promotionsContentContainerStyle}
                largeHeaderStyle={styles.promotionsHeaderStyle}
                largeListStyle={styles.promotionsLargeListStyle}
                locationPrefix="order_guide_carousel"
                maxLength={maxLength}
                onNavigateToDetails={(promotionUUID) => {
                  navigation.navigate(
                    ORDER_GUIDE_ROUTES.VolumePromotionDetail,
                    {
                      promotionUUID,
                    },
                  );
                }}
                onSeeAllPress={() => {
                  Analytics.track(
                    'order_guide_carousel.promotions_see_all.press',
                  );
                  navigation.navigate(ORDER_GUIDE_ROUTES.VolumePromotionsList);
                }}
                titleOverride={titleOverride}
                visibleUUIDs={visiblePromotionUUIDs}
              />
            </View>
          ) : null;
        case OrderGuideCarouselType.INTEREST:
          return itemRecommendations.length ? (
            <View style={[styles.carouselWrapper, carouselWrapperStyle]}>
              <CatalogItemCarousel
                carouselType={OrderGuideCarouselType.INTEREST}
                count={maxLength ?? itemRecommendations.length}
                listItems={itemRecommendations.map((ir) => ir?.metadata!.item)}
                title={titleOverride ?? i18n.t('based_on_interests')}
                trackingSource="interest_order_guide_carousel"
              />
            </View>
          ) : null;
        case OrderGuideCarouselType.SAMPLE:
          return samples.notRequested.length ? (
            <View style={[styles.carouselWrapper, carouselWrapperStyle]}>
              <CatalogItemCarousel
                carouselType={OrderGuideCarouselType.SAMPLE}
                count={maxLength ?? samples.notRequested.length}
                hasFeaturedTag
                listItems={samples.notRequested}
                title={titleOverride ?? i18n.t('sample_request.carousel_title')}
                trackingSource="sample_order_guide_carousel"
              />
            </View>
          ) : null;
        default:
          return null;
      }
    },
    [
      brandCarouselItems,
      carouselWrapperStyle,
      hideDisplayAd,
      itemRecommendations,
      navigation,
      samples,
      styles,
      visiblePromotionUUIDs,
    ],
  );

  const defaultColumnCount = useOrderableItemColumnCount();
  const transformedSectionData = useMemo(() => {
    if (
      !orderGuideCarouselConfig ||
      searchText ||
      partitionFilter ||
      isOfflineModeActive
    ) {
      return sectionData;
    }

    // Map each carousel position to an array of corresponding carousel types
    const carouselIndexMap = Object.entries(orderGuideCarouselConfig).reduce<{
      [index: number]: OrderGuideCarouselConfigType[];
    }>((acc, [type, config]) => {
      const listPosition = config?.list_position;

      if (typeof listPosition === 'number') {
        const carouselTypeConfig = {
          config,
          type: type as OrderGuideCarouselType,
        };

        if (acc[listPosition]) {
          acc[listPosition].push(carouselTypeConfig);
        } else {
          acc[listPosition] = [carouselTypeConfig];
        }
      }

      return acc;
    }, {});

    const createCarouselListItem = (
      type: OrderGuideCarouselType,
      config: OrderGuideCarouselSettings,
    ): OrderGuideCarouselOption => ({
      metadata: {
        position: config.list_position,
        renderCarousel: () =>
          getCarouselComponentByType(
            type,
            config.title_override,
            config.max_length,
          ),
        type,
      },
      title: config.title_override ?? '',
      value: ListItemType.Carousel,
    });

    const removeSeparatorsBeforeCarousel = (
      newSection: OrderGuideItemSection,
      listSeparatorsToRemove: number,
    ) => {
      for (let j = 0; j < listSeparatorsToRemove; j++) {
        // Subtract 2 to find the last option before the carousel
        const previousOption = newSection.data[
          newSection.data.length - 2 - j
        ] as OrderGuideItemListOption;
        if (previousOption) {
          previousOption.hideTrailingItemSeparator = true;
        }
      }
    };

    let itemCount = 0;
    // Represents how many items exists above carousel
    // that did not make up a complete row in grid view
    let carouselIndexOffset = 0;

    return sectionData.reduce<OrderGuideItemSection[]>(
      (acc, section, sectionIndex) => {
        const newSection: OrderGuideItemSection = {
          data: [],
          key: section.key,
        };

        // Add carousels at top of each section
        const firstPositionCarouselConfigs = carouselIndexMap[itemCount];
        if (firstPositionCarouselConfigs) {
          firstPositionCarouselConfigs.forEach(({ type, config }) => {
            newSection.data.push(createCarouselListItem(type, config));
            delete carouselIndexMap[itemCount];
          });
        }

        section.data.forEach((option, i) => {
          // Add order guide list item
          newSection.data.push(option);
          itemCount++;

          // Add carousel within each section if carousel should be rendered
          const currentIndexCarouselConfigs = carouselIndexMap[itemCount];
          if (currentIndexCarouselConfigs) {
            currentIndexCarouselConfigs.forEach(({ type, config }) => {
              const carouselListItem = createCarouselListItem(type, config);
              if (carouselListItem.metadata?.renderCarousel()) {
                newSection.data.push(carouselListItem);

                const lastRowRemainingCount =
                  (itemCount - carouselIndexOffset) % defaultColumnCount;
                // If previous row was full, remove the entire column's separators
                const listSeparatorsToRemove =
                  lastRowRemainingCount || defaultColumnCount;

                removeSeparatorsBeforeCarousel(
                  newSection,
                  listSeparatorsToRemove,
                );
                // Update offset for next carousel if previous row was not full
                carouselIndexOffset =
                  lastRowRemainingCount || carouselIndexOffset;
              }
              delete carouselIndexMap[itemCount];
            });
          }

          // Add remaining carousels to very end of order guide list sections
          if (
            i === section.data.length - 1 &&
            sectionIndex === sectionData.length - 1
          ) {
            const remainingCarousels = Object.values(carouselIndexMap).flat();
            remainingCarousels.forEach(({ type, config }) => {
              newSection.data.push(createCarouselListItem(type, config));
            });

            const lastRowRemainingCount =
              (itemCount - carouselIndexOffset) % defaultColumnCount;
            const listSeparatorsToRemove =
              lastRowRemainingCount || defaultColumnCount;

            removeSeparatorsBeforeCarousel(newSection, listSeparatorsToRemove);
          }
        });

        // Update offset at end of each section after carousel insertions
        carouselIndexOffset =
          (itemCount + defaultColumnCount - carouselIndexOffset) %
            defaultColumnCount || carouselIndexOffset;

        acc.push(newSection);
        return acc;
      },
      [],
    );
  }, [
    defaultColumnCount,
    getCarouselComponentByType,
    isOfflineModeActive,
    orderGuideCarouselConfig,
    partitionFilter,
    searchText,
    sectionData,
  ]);

  const combinedSectionData: OrderGuideItemSection[] = useMemo(() => {
    if (noOrderGuideSearchResults && !orderGuideCatalogSearchEnabled) {
      return [{ data: [], key: EMPTY_ORDER_GUIDE_KEY }];
    }

    if (debouncedSearchCatalogLoading || Array.isArray(catalogSearchItems)) {
      return [
        ...(paidSearchItems?.length
          ? [{ data: paidSearchItems, key: PAID_SEARCH_RESULTS_KEY }]
          : []),
        ...(noOrderGuideSearchResults
          ? [{ data: [], key: EMPTY_ORDER_GUIDE_KEY }]
          : transformedSectionData),
        {
          data: catalogSearchItems || [],
          key: CATALOG_SEARCH_RESULT_KEY,
        },
      ];
    }

    return transformedSectionData;
  }, [
    catalogSearchItems,
    debouncedSearchCatalogLoading,
    noOrderGuideSearchResults,
    orderGuideCatalogSearchEnabled,
    paidSearchItems,
    transformedSectionData,
  ]);

  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

  const combinedSectionDataWithFlatIndex = useMemo(() => {
    let fullIndex = 0;
    let navigableIndex = 0;
    /**
     * Add flatIndex to each item in the section data to be able to focus on them.
     * We're explicitly accessing the data property of each section
     * instead of using the spread operator to avoid undefined properties
     * detailed explanation here: https://github.com/usepepper/restaurant-app/pull/2153#discussion_r1767554606
     * please do not change this to a spread operator
     */
    return combinedSectionData.map((section) => ({
      ...section,
      data: section.data.map((item) => {
        item.fullIndex = fullIndex++;
        // ignore carousel items when navigating via keyboard shortcuts
        if (item.value !== ListItemType.Carousel) {
          item.flatIndex = navigableIndex++;
        } else {
          item.flatIndex = undefined;
        }
        return item;
      }),
    }));
  }, [combinedSectionData]);

  const flatData = useMemo(() => {
    return combinedSectionDataWithFlatIndex.flatMap((section) => section.data);
  }, [combinedSectionDataWithFlatIndex]);

  const keyboardNavigableItems = useMemo(() => {
    return flatData.filter((item) => item.flatIndex !== undefined);
  }, [flatData]);

  const centerItemAtIndex = (index: number) => {
    if (listRef.current && flatData.length > 0) {
      const itemHeight = HORIZONTAL_SELECTOR_HEIGHT;
      const halfScreenHeight = height / 2;
      const offset = index * itemHeight - halfScreenHeight + itemHeight / 2;
      const viewOffset = Math.max(0, offset);

      listRef.current.scrollToIndex({ index, viewOffset });
    }
  };

  const focusItemAtIndex = (index: number) => {
    if (index >= 0 && index < keyboardNavigableItems.length) {
      const item = keyboardNavigableItems[index];
      setFocusedIndex(item.fullIndex!);
      centerItemAtIndex(item.fullIndex!);
    }
  };

  const resetFocus = useCallback(() => {
    setFocusedIndex(null);
    listRef.current?.scrollToTop();
  }, []);

  const onOrderGuideVisible = useCallback(() => {
    PerfTiming.end(PerfTimingEvent.FIRST_SCREEN_VISIBLE, {
      restaurantUUID: selectedRestaurantRelationship.uuid,
      screenType: 'order_guide',
      supplierUUID: selectedRestaurantRelationship.supplierUUID,
    });
    endSpan(loadSpanRef.current, {
      payload: {
        itemCount: orderGuideItemCount,
      },
    });
    loadSpanRef.current = undefined;

    setFeatureSpanAttributes(Feature.OrderGuide, {
      [SpanAttribute.OrderGuideItemCount]: orderGuideItemCount,
    });
    setOrderGuideVisible(true);
  }, [
    orderGuideItemCount,
    selectedRestaurantRelationship.uuid,
    selectedRestaurantRelationship.supplierUUID,
  ]);

  const moveFocusUp = () => {
    if (focusedIndex === null) {
      focusItemAtIndex(keyboardNavigableItems.length - 1);
    } else {
      const currentNavigableIndex = keyboardNavigableItems.findIndex(
        (item) => item.fullIndex === focusedIndex,
      );
      const newIndex =
        (currentNavigableIndex - 1 + keyboardNavigableItems.length) %
        keyboardNavigableItems.length;
      focusItemAtIndex(newIndex);
    }
  };

  const moveFocusDown = () => {
    if (focusedIndex === null) {
      focusItemAtIndex(0);
    } else {
      const currentNavigableIndex = keyboardNavigableItems.findIndex(
        (item) => item.fullIndex === focusedIndex,
      );
      const newIndex =
        (currentNavigableIndex + 1) % keyboardNavigableItems.length;
      focusItemAtIndex(newIndex);
    }
  };

  useKeyboardShortcuts(
    AllowedShortcutNumbers,
    (_, handler) => {
      const key = handler.keys?.join().toLowerCase();
      const numberPressed = numberKeyMap[`${Key.Alt}+${key}`];
      if (numberPressed !== undefined) {
        const index = numberPressed - 1;
        focusItemAtIndex(index);
      }
    },
    [flatData],
  );

  useKeyboardShortcuts(AllowedShortcuts.ArrowUp, moveFocusUp, [
    focusedIndex,
    flatData,
  ]);

  useKeyboardShortcuts(AllowedShortcuts.ArrowDown, moveFocusDown, [
    focusedIndex,
    flatData,
  ]);

  const showSmartOrderButton = !loading && !hasAnyItemQuantity;

  const showFilters = !loading && !renderFullEmptyState;

  const onSearchTextChange = useCallback(
    (value: string | null) => {
      resetFocus();
      if (value) {
        onDisplayTextChange(value);
        setPromotionFilterText(value);
        setAdFilterText(value);

        if (orderGuideCatalogSearchEnabled) {
          setDebouncedSearchCatalogLoading(true);
          debouncedSearchCatalog(value);
        }
      } else {
        clearDisplayAdSearch();
        clearOrderGuideSearch();
        clearPromotionSearch();

        if (orderGuideCatalogSearchEnabled) {
          setDebouncedSearchCatalogLoading(false);
          searchCatalog('');
          debouncedSearchCatalog.cancel();
        }
      }
    },
    [
      clearDisplayAdSearch,
      clearOrderGuideSearch,
      clearPromotionSearch,
      debouncedSearchCatalog,
      onDisplayTextChange,
      orderGuideCatalogSearchEnabled,
      resetFocus,
      searchCatalog,
      setAdFilterText,
      setPromotionFilterText,
    ],
  );

  useTabBlurListener(
    useCallback(() => onSearchTextChange(null), [onSearchTextChange]),
  );

  const onSearchFocus = useCallback(() => {
    if (orderGuideUnifiedSearchEnabled) {
      // Stay in Order Guide when offline
      if (isOfflineModeActive) {
        return;
      }

      navigation.navigate(ORDER_GUIDE_ROUTES.OrderGuideUnifiedSearch);
    }
  }, [orderGuideUnifiedSearchEnabled, isOfflineModeActive, navigation]);

  const orderGuideSearchInputPlaceholder = useMemo(() => {
    if (orderGuideUnifiedSearchEnabled) {
      return i18n.t('search');
    }
    if (!selectedCollection || selectedCollection.isDefault) {
      return i18n.t('order_guide.search_filter_placeholder');
    }
    return i18n.t('search_within_partition_placeholder', {
      partition: selectedCollection.name,
    });
  }, [orderGuideUnifiedSearchEnabled, selectedCollection]);

  const isProspect =
    selectedRestaurantRelationship.chatStatus === ChatStatus.PROSPECT;
  const isSupplierEmployee = Boolean(supplierEmployeeRole);

  const pageContainerProps = useMemo<PageContainerProps>(() => {
    const onScanPress =
      scannerOperations.length > 0
        ? () => {
            scannerOperations.length > 1
              ? setShowScanSelectionActionSheet(true)
              : navigation.push(ORDER_GUIDE_ROUTES.Scan);
          }
        : undefined;
    const showProminentScanButton =
      Boolean(onScanPress) && prominentScanningEntryEnabled;

    const actions: Action[] = [
      {
        // create container with offset because overflow: visible doesn't work
        // properly on android.
        // Offset the padding with -4 margin to maintain the same space between
        // icons
        // https://github.com/facebook/react-native/issues/12534
        component: (
          <TouchableOpacity
            hitSlop={ICON_BUTTON_HIT_SLOP}
            onPress={() => {
              setIsSettingsOpen(true);
            }}
            testID="order-guide-settings-icon"
          >
            <EditIcon fill={theme.colors.primary} />
          </TouchableOpacity>
        ),
      },
    ];

    if (!isSmallLayout) {
      actions.push({
        component: (
          <View style={styles.searchHeaderWrapper}>
            <ItemSearchHeaderAction
              editable={!orderGuideUnifiedSearchEnabled || isOfflineModeActive}
              isLoading={searchLoading}
              onPress={onSearchFocus}
              onSubmit={onSearchTextChange}
              placeholder={orderGuideSearchInputPlaceholder}
              submitOnChange
            />
          </View>
        ),
      });

      if (onScanPress) {
        actions.push({
          Icon: ScanIcon,
          iconProps: {
            stroke: theme.sk.color.icon.primary,
          },
          onPress: onScanPress,
          testID: 'barcode-scanner-icon',
        });
      }
    }

    return {
      containerActions: actions,
      navigationHeaderProps: {
        ...baseNavigationHeaderProps,
        actions,
        after: isSmallLayout && !isProspect ? <FulfillmentPill /> : null,
        containerStyle: [
          baseNavigationHeaderProps.containerStyle,
          isSmallLayout ? null : styles.navHeaderContainerLarge,
        ],
        contentFooter: !isSmallLayout ? <FulfillmentAvailability /> : null,
        contentStyle: [
          baseNavigationHeaderProps.contentStyle,
          isSmallLayout ? styles.contentReset : null,
        ],
        responsive: true,
        subheader: (
          <View style={styles.subheaderContainer}>
            {isSmallLayout ? (
              <View style={showProminentScanButton ? styles.searchRow : null}>
                <SearchOrderGuideInput
                  containerStyle={
                    showProminentScanButton ? styles.searchInputNarrow : null
                  }
                  editable={
                    !orderGuideUnifiedSearchEnabled || isOfflineModeActive
                  } // on unified search behaves as icon, but allow search in offline mode
                  isLoading={searchLoading}
                  onChangeText={onSearchTextChange}
                  onPress={onSearchFocus}
                  placeholder={orderGuideSearchInputPlaceholder}
                  rightElement={
                    onScanPress && !showProminentScanButton ? (
                      <TouchableOpacity
                        hitSlop={BARCODE_SCANNER_HIT_SLOP}
                        onPress={onScanPress}
                        testID="barcode-scanner-button"
                      >
                        <ScanIcon stroke={theme.sk.color.icon.primary} />
                      </TouchableOpacity>
                    ) : null
                  }
                  testID="search-order-guide-input"
                  value={searchText}
                />
                {showProminentScanButton ? (
                  <TouchableOpacity
                    onPress={onScanPress}
                    style={styles.prominentScanButton}
                    testID="barcode-scanner-button"
                  >
                    <ScanIcon
                      height={20}
                      stroke={theme.sk.color.icon.primary}
                      width={20}
                    />
                    <Text style={styles.prominentScanButtonText}>
                      {i18n.t('scan')}
                    </Text>
                  </TouchableOpacity>
                ) : null}
              </View>
            ) : null}
            {showFilters ? (
              <>
                {showCategoryFilters ? (
                  <View style={styles.subheaderFiltersContainer}>
                    {itemGroupsEnabled ? (
                      <ItemGroupSelector
                        data={categoryItemGroupsInSelector}
                        onItemGroupPress={setCategoryItemGroupFilter}
                        selectedItemGroup={categoryItemGroupFilter}
                        trackingKey="multi_order_guide_screen.item_group_selector"
                      />
                    ) : (
                      <CategorySelector
                        categories={categories}
                        onCategoryPress={setCategoryFilter}
                        selectedCategory={categoryFilter}
                      />
                    )}
                  </View>
                ) : null}
                {onlyHasDefaultPartition ? null : (
                  <PartitionSelector
                    containerStyle={styles.partitionBorder}
                    partitionFilter={partitionFilter}
                    partitions={partitionFilters}
                    setPartitionFilter={(nextFilter) => {
                      // because of the sticky header bug workaround, the partition filter
                      // resets the list and the scroll position
                      // https://github.com/Shopify/flash-list/issues/615
                      // reset scroll position of page container to avoid white space
                      setPartitionFilter(nextFilter);
                      pageContainerRef.current?.resetScrollPosition();
                    }}
                    showAllFilter
                  />
                )}
              </>
            ) : null}
          </View>
        ),
        title: canSwitchCollections ? (
          <MultiOrderGuideTitle />
        ) : (
          i18n.t('order_guide')
        ),
      },
      onScanPress,
      statusBarColor: theme.colors.background,
    };
  }, [
    baseNavigationHeaderProps,
    canSwitchCollections,
    categories,
    categoryFilter,
    categoryItemGroupFilter,
    isOfflineModeActive,
    isProspect,
    isSmallLayout,
    categoryItemGroupsInSelector,
    itemGroupsEnabled,
    navigation,
    onlyHasDefaultPartition,
    onSearchFocus,
    onSearchTextChange,
    partitionFilter,
    partitionFilters,
    prominentScanningEntryEnabled,
    scannerOperations,
    searchLoading,
    searchText,
    setCategoryFilter,
    setCategoryItemGroupFilter,
    setPartitionFilter,
    showCategoryFilters,
    showFilters,
    styles,
    theme,
    orderGuideUnifiedSearchEnabled,
    orderGuideSearchInputPlaceholder,
  ]);

  const firstPaidSearchItem = paidSearchItems?.[0];

  const isCatalogLimited =
    useDynamicConfigValue('application_type') === ApplicationType.CATALOG_ONLY;

  const listItemProps = useMemo<
    Partial<ListItemProps<ItemListOption>> | undefined
  >(() => {
    const setSwipeableProps = Platform.OS !== 'web' && !showListView;
    return {
      renderListItemHeader: (option, columnCount) => {
        return Boolean(findFirstPaidSearchSku(option)) &&
          // Show paid search header for all items in multi column grid layouts
          // Otherwise only show for first paid search item
          ((typeof columnCount === 'number' && columnCount > 1) ||
            option === firstPaidSearchItem) ? (
          <PaidSearchItemHeader
            showListView={showListView}
            style={showListView ? styles.paidSearchListViewMargin : null}
          />
        ) : null;
      },
      renderMainContent: (option) => {
        const isCatalogSearchResult = option.metadata!.catalogSearchResult;
        const paidAdResult = option.metadata!.paidSearch;

        if (isCatalogSearchResult) {
          return (
            <OrderableItemListItemWithRowQuantity
              containerStyle={
                !showListView
                  ? null
                  : paidAdResult
                    ? styles.paidSearchListViewMargin
                    : styles.catalogListViewMargin
              }
              isFocused={option.fullIndex === focusedIndex}
              option={option}
              renderOrderGuideButton={
                customOrderGuideEditingEnabled
                  ? (variantPackUUID: string) => (
                      <MultiOrderGuideButton
                        openPartitionSelector={() => {
                          setPartitionSelectorVariantPackUUID(variantPackUUID);
                        }}
                        variantPackUUID={variantPackUUID}
                      />
                    )
                  : undefined
              }
              showListView={showListView}
              source="order_guide_catalog_search"
              updateQuantity={(updateQuantityData) => {
                debouncedUpdateQuantity(updateQuantityData, {
                  source: 'order_guide_catalog_search',
                });
              }}
            />
          );
        }

        return (
          <OrderGuideListItem
            isFocused={option.fullIndex === focusedIndex}
            option={option}
            setDSRToolingActionSheetItemMetadata={
              setDSRToolingActionSheetItemMetadata
            }
          />
        );
      },
      swipeableProps: setSwipeableProps
        ? {
            onSwipeableRightWillOpen: (_, swipeableRef) => {
              if (
                swipeableItemRef.current &&
                swipeableItemRef.current !== swipeableRef
              ) {
                closeSwipeable();
              }
              swipeableItemRef.current = swipeableRef;
            },
            renderRightActions: (listItem) => {
              if (
                listItem.metadata?.catalogSearchResult ||
                (!customOrderGuideEditingEnabled && isCatalogLimited)
              ) {
                return null;
              }
              return (
                <View
                  style={[
                    styles.rightActionContainer,
                    {
                      width:
                        ACTION_BUTTON_WIDTH *
                        (isCatalogLimited || !customOrderGuideEditingEnabled
                          ? 1
                          : 2),
                    },
                  ]}
                >
                  {!isCatalogLimited ? (
                    <EssentialItemRightAction
                      isEssential={isRowEssential(listItem.metadata!)}
                      onPress={() => {
                        editEssentialItem(listItem.metadata!);
                        closeSwipeable();
                      }}
                      trackingData={{
                        name: 'essential_item_right_action_button',
                        properties: {
                          variantPackUUID: listItem.metadata?.variantPackUUID,
                        },
                      }}
                    />
                  ) : null}
                  {customOrderGuideEditingEnabled ? (
                    <AddNoteRightAction
                      onPress={() => {
                        editItemNotes(listItem.metadata!);
                        closeSwipeable();
                      }}
                      trackingData={{
                        name: 'add_note_right_action_button',
                        properties: {
                          variantPackUUID: listItem.metadata?.variantPackUUID,
                        },
                      }}
                    />
                  ) : null}
                </View>
              );
            },
          }
        : undefined,
    };
  }, [
    closeSwipeable,
    customOrderGuideEditingEnabled,
    debouncedUpdateQuantity,
    editEssentialItem,
    editItemNotes,
    focusedIndex,
    firstPaidSearchItem,
    isRowEssential,
    isCatalogLimited,
    showListView,
    styles,
  ]);

  const renderPromotions = () => {
    if (!partitionFilter) {
      return (
        <View style={styles.flex}>
          <VolumePromotionsCarousel
            locationPrefix="order_guide"
            onNavigateToDetails={(promotionUUID) => {
              navigation.navigate(ORDER_GUIDE_ROUTES.VolumePromotionDetail, {
                promotionUUID,
              });
            }}
            onSeeAllPress={() => {
              Analytics.track('order_guide.promotions_see_all.press');
              navigation.navigate(ORDER_GUIDE_ROUTES.VolumePromotionsList);
            }}
            visibleUUIDs={visiblePromotionUUIDs}
          />
        </View>
      );
    }
    return null;
  };

  return (
    <TimeToFullDisplay record={orderGuideVisible}>
      <OrderableItemScrollbarContainer
        onScrollToIndex={(index) => {
          listRef.current?.scrollToIndex({ animated: false, index });
        }}
        sections={sectionData}
        sortOrder={orderGuideSortOrder}
      >
        <PageContainer
          {...pageContainerProps}
          animateNavigationHeader
          loading={searchLoading}
          ref={pageContainerRef}
          showNavigationHeader
        >
          {(scrollViewProps, scrollY) => (
            <>
              {loading ? (
                <View
                  style={[
                    styles.loadingContainer,
                    scrollViewProps.contentContainerStyle,
                  ]}
                >
                  <ActivityIndicator testID="loading-indicator" />
                </View>
              ) : !selectedCollection ? (
                <View style={styles.noCollectionState} />
              ) : renderFullEmptyState ? (
                <EmptyOrderGuideContent
                  contentContainerStyle={scrollViewProps.contentContainerStyle}
                  displayAd={hideDisplayAd ? null : displayAd}
                  navigateToCatalog={() =>
                    navigation.navigate(APP_ROUTES.Catalog)
                  }
                />
              ) : renderPartitionEmptyState ? (
                <ScrollView {...scrollViewProps}>
                  <View style={[styles.partitionEmptyState]}>
                    <Text
                      style={[
                        TYPOGRAPHY.medium,
                        styles.partitionEmptyStateText,
                      ]}
                    >
                      {i18n.t('empty_partition.message')}
                    </Text>
                    <Button
                      onPress={() => {
                        navigation.navigate(APP_ROUTES.Catalog);
                      }}
                      trackingData={{
                        name: 'multi_order_guide.partition_empty_state_button',
                      }}
                    >
                      {i18n.t('empty_partition.button_text')}
                    </Button>
                  </View>
                </ScrollView>
              ) : renderSearchEmptyStateWithCatalogSearch ? (
                <View
                  style={[
                    scrollViewProps.contentContainerStyle,
                    styles.catalogSearchFullEmptyState,
                  ]}
                >
                  <Text
                    style={[
                      TYPOGRAPHY.medium,
                      styles.catalogSearchEmptyStateText,
                    ]}
                  >
                    {i18n.t('order_guide_catalog_search.empty_state', {
                      searchTerm: searchText,
                    })}
                  </Text>
                </View>
              ) : (
                <OrderGuideList
                  isFocused={listItemProps?.item?.flatIndex === focusedIndex}
                  // Rerender the entire list when the partition filter changes
                  // to force sticky headers to update
                  // https://github.com/Shopify/flash-list/issues/615
                  key={partitionFilter?.value || null}
                  ListHeaderComponent={
                    !hideDisplayAd ? (
                      <DisplayAd
                        displayAd={displayAd}
                        visibleUUIDs={visibleDisplayAdUUIDs}
                      />
                    ) : (
                      renderPromotions()
                    )
                  }
                  listItemProps={listItemProps}
                  listRef={listRef}
                  onVisible={onOrderGuideVisible}
                  renderSectionHeader={(item, target) => {
                    const isCatalogSearchResult =
                      item.title === CATALOG_SEARCH_RESULT_KEY;

                    if (isCatalogSearchResult && target !== 'StickyHeader') {
                      const noSearchResults =
                        (catalogSearchItems || []).length === 0;

                      if (noSearchResults && paidSearchItems?.length) {
                        return null;
                      }

                      return (
                        <View
                          style={[
                            styles.catalogHeader,
                            noSearchResults ? styles.catalogHeaderEmpty : null,
                          ]}
                          testID={`${target}-${item.title}`}
                        >
                          <LoadingText
                            loading={debouncedSearchCatalogLoading}
                            loadingText={i18n.t('also_searching_catalog')}
                            textStyle={[
                              TYPOGRAPHY.small,
                              styles.catalogHeaderText,
                            ]}
                          >
                            {noSearchResults
                              ? i18n.t('no_results_from_catalog')
                              : i18n.t('results_from_catalog')}
                          </LoadingText>
                        </View>
                      );
                    }

                    if (item.title === EMPTY_ORDER_GUIDE_KEY) {
                      if (target === 'StickyHeader') {
                        return null;
                      }

                      const isContentAbove =
                        !hideDisplayAd || paidSearchItems?.length;

                      return (
                        <View
                          style={[
                            styles.catalogSearchOGEmptyState,
                            isContentAbove
                              ? styles.catalogSearchOGEmptyStateWithAds
                              : null,
                          ]}
                        >
                          <Text
                            style={[
                              TYPOGRAPHY.medium,
                              styles.catalogSearchEmptyStateText,
                            ]}
                          >
                            {i18n.t('order_guide_search_header.empty_state', {
                              searchTerm: searchText,
                            })}
                          </Text>
                        </View>
                      );
                    }

                    if (item.title === PAID_SEARCH_RESULTS_KEY) {
                      if (target === 'StickyHeader' && hideDisplayAd) {
                        return null;
                      }
                      return <View style={styles.stickyHeaderMargin} />;
                    }

                    // only other sticky header is the partition header
                    // and if we only have one partition, hide the header
                    if (onlyHasDefaultPartition && !isCatalogSearchResult) {
                      return null;
                    }

                    const categoryListHeight =
                      showCategoryFilters && categories.length
                        ? HORIZONTAL_SELECTOR_HEIGHT
                        : 0;

                    const partitionFilterHeight = onlyHasDefaultPartition
                      ? 0
                      : HORIZONTAL_SELECTOR_HEIGHT;

                    const smallLayoutStyle = {
                      marginTop:
                        partitionFilterHeight +
                        categoryListHeight +
                        SEARCH_BAR_HEIGHT +
                        top,
                    };

                    const nonSmallLayoutStyle = {
                      marginTop:
                        partitionFilterHeight +
                        categoryListHeight +
                        CONTENT_PADDING_TOP +
                        LARGE_LAYOUT_NAV_PADDING_BOTTOM,
                    };

                    let stickyHeaderStyle =
                      target === 'StickyHeader'
                        ? Platform.OS === 'web'
                          ? null
                          : isSmallLayout
                            ? smallLayoutStyle
                            : nonSmallLayoutStyle
                        : styles.stickyHeaderMargin;

                    return (
                      <View
                        style={[styles.stickyHeader, stickyHeaderStyle]}
                        testID={`${target}-${item.title}`}
                      >
                        <Text
                          style={[
                            TYPOGRAPHY.small,
                            FONTS.primaryBold,
                            isCatalogSearchResult
                              ? styles.stickyHeaderUppercase
                              : null,
                          ]}
                        >
                          {isCatalogSearchResult
                            ? i18n.t('results_from_catalog')
                            : item.title}
                        </Text>
                      </View>
                    );
                  }}
                  scrollViewProps={scrollViewProps}
                  // @ts-ignore
                  sections={combinedSectionDataWithFlatIndex}
                  setDSRToolingActionSheetItemMetadata={
                    setDSRToolingActionSheetItemMetadata
                  }
                  showSeparatorBetweenSections={sections.length !== 0}
                />
              )}
              {showSmartOrderButton ? (
                <SmartOrderButton
                  onPress={() => setShowSmartOrderSheet(true)}
                  scrollY={scrollY}
                />
              ) : null}
              {isProspect && isSupplierEmployee ? (
                // For prospects, render the create price list button (supplier employees only)
                <SharePriceQuoteButton
                  onPress={() => setIsSharePriceQuoteOpen(true)}
                />
              ) : (
                // For non-prospects or restaurant employees, render Order Button
                <ViewOrderButton />
              )}
            </>
          )}
        </PageContainer>
      </OrderableItemScrollbarContainer>
      {dsrToolingActionSheetItemMetadata ? (
        <DSRToolingActionSheet
          itemMetadata={dsrToolingActionSheetItemMetadata}
          onClose={() => {
            setDSRToolingActionSheetItemMetadata(null);
          }}
        />
      ) : null}
      {isSettingsOpen ? (
        <OrderGuideSettingsActionSheet
          onClose={() => setIsSettingsOpen(false)}
        />
      ) : null}
      {partitionSelectorVariantPackUUID ? (
        <OrderGuidePartitionSectionActionSheet
          onClose={() => setPartitionSelectorVariantPackUUID(null)}
          variantPackUUID={partitionSelectorVariantPackUUID}
        />
      ) : null}
      {showSmartOrderSheet ? (
        <SmartOrderActionSheet onClose={() => setShowSmartOrderSheet(false)} />
      ) : null}
      {showScanSelectionActionSheet ? (
        <ScannerOperationsActionSheet
          onClose={() => setShowScanSelectionActionSheet(false)}
          operations={scannerOperations}
        />
      ) : null}
      {sampleRequestToFollowUp ? (
        <SampleRequestFollowUp
          metadata={sampleRequestToFollowUp.metadata!}
          onClose={onSampleRequestFollowUpClose}
          sampleRequest={sampleRequestToFollowUp.metadata!.sampleRequest!}
        />
      ) : null}
      {isSharePriceQuoteOpen ? (
        <SharePriceQuoteActionSheet
          isVisible
          onClose={() => setIsSharePriceQuoteOpen(false)}
          renderInDraggableSheet={Platform.OS !== 'web'}
        />
      ) : null}
    </TimeToFullDisplay>
  );
};

const ACTION_BUTTON_WIDTH = 72;
const useStyles = createStyles((theme) => ({
  brandCarouselStyle: {
    paddingHorizontal: 0,
  },
  carouselWrapper: {
    backgroundColor: theme.sk.color.bg.secondary.default,
    flex: 1,
    marginVertical: 8,
    paddingBottom: 8,
  },
  carouselWrapperLarge: {
    borderRadius: BORDER_RADIUS_SMALL,
    marginHorizontal: SCREEN_PADDING_LARGE,
    paddingHorizontal: 8,
  },
  carouselWrapperTablet: {
    borderRadius: BORDER_RADIUS_SMALL,
    marginHorizontal: SCREEN_PADDING_SMALL,
    paddingHorizontal: 8,
  },
  catalogHeader: {
    backgroundColor: theme.colors.white,
    flex: 1,
    marginHorizontal: 16,
    paddingTop: 24,
  },
  catalogHeaderEmpty: {
    paddingBottom: 32,
  },
  catalogHeaderText: {
    ...FONTS.primaryBold,
    color: theme.colors.inactiveText,
    textTransform: 'uppercase',
  },
  catalogListViewMargin: {
    marginHorizontal: CATALOG_LIST_VIEW_MARGIN,
  },
  catalogSearchEmptyStateText: {
    textAlign: 'center',
  },
  catalogSearchFullEmptyState: {
    flex: 1,
    marginHorizontal: SCREEN_PADDING,
  },
  catalogSearchOGEmptyState: {
    flex: 1,
    marginBottom: 16,
    marginHorizontal: 16,
  },
  catalogSearchOGEmptyStateWithAds: {
    marginTop: 24,
  },
  contentReset: {
    height: CONTENT_HEIGHT - SEARCH_BAR_VERTICAL_MARGIN,
    marginBottom: 0,
    marginTop: 4,
  },
  flex: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    marginTop: 24,
  },
  navHeaderContainerLarge: {
    paddingBottom: LARGE_LAYOUT_NAV_PADDING_BOTTOM,
  },
  noCollectionState: {
    backgroundColor: theme.colors.background,
    flex: 1,
  },
  paidSearchListViewMargin: {
    marginHorizontal: CATALOG_LIST_VIEW_MARGIN,
    marginTop: 0,
  },
  partitionBorder: {
    borderTopColor: theme.colors.stroke,
    borderTopWidth: 1,
  },
  partitionEmptyState: {
    alignSelf: 'center',
    flex: 1,
    marginHorizontal: SCREEN_PADDING,
    marginTop: 40,
    maxWidth: LARGE_LAYOUT_CONTENT_MAX_WIDTH,
  },
  partitionEmptyStateText: {
    marginBottom: 16,
    textAlign: 'center',
  },
  prominentScanButton: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.primary.default,
    borderColor: theme.sk.color.border.button.secondary.default,
    borderRadius: theme.sk.radii.radiusMedium,
    borderWidth: 1,
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceXsmall,
    height: TEXT_INPUT_HEIGHT,
    marginRight: theme.sk.spacing.spaceMedium,
    paddingHorizontal: theme.sk.spacing.spaceSMedium,
  },
  prominentScanButtonText: {
    ...(theme.sk.typography.label.md as TextStyle),
    color: theme.sk.color.text.primary,
  },
  promotionsContentContainerStyle: {
    paddingVertical: 0,
  },
  promotionsHeaderStyle: {
    marginBottom: 8,
    marginHorizontal: 8,
  },
  promotionsLargeListStyle: {
    marginHorizontal: 0,
  },
  rightActionContainer: {
    flexDirection: 'row',
  },
  searchHeaderWrapper: {
    marginLeft: 12,
  },
  searchInputNarrow: {
    flex: 1,
    marginRight: theme.sk.spacing.spaceSmall,
  },
  searchRow: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  stickyHeader: {
    backgroundColor: theme.colors.background,
    paddingHorizontal: 16,
    paddingVertical: 8,
    width: '100%',
  },
  stickyHeaderMargin: {
    marginVertical: 8,
  },
  stickyHeaderUppercase: {
    textTransform: 'uppercase',
  },
  subheaderContainer: {
    backgroundColor: theme.colors.white,
  },
  subheaderFiltersContainer: {
    borderTopColor: theme.colors.stroke,
    borderTopWidth: 1,
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
}));
